import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, PLATFORM_ID, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';

import { AssetUrlPipe } from '../../../core/api/asset-url.pipe';
import { FrontendStore } from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { BigSaleBanner } from '../../../shared/big-sale-banner/big-sale-banner';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';
import { StoreLogo } from '../../../shared/store-logo/store-logo';

const PAGE_PATH = '/submit-coupon';
const PAGE_TITLE = 'Submit a Coupon | SavingArena';
const PAGE_DESCRIPTION =
  'Found a better deal? Share your coupon code with the SavingArena community and help others save.';

interface StoreOption {
  slug: string;
  name: string;
  logoImagePath: string;
}

function toStoreOption(store: FrontendStore): StoreOption {
  return {
    slug: (store.slug ?? '').trim(),
    name: (store.name ?? '').trim(),
    logoImagePath: (store.logoImagePath ?? '').trim(),
  };
}

@Component({
  selector: 'app-submit-coupon-page',
  imports: [Breadcrumb, ReactiveFormsModule, AssetUrlPipe, BigSaleBanner, DevFlag, StoreLogo],
  templateUrl: './submit-coupon-page.html',
  styleUrl: './submit-coupon-page.scss',
})
export class SubmitCouponPage {
  protected readonly crumbs = breadcrumbTrail({ label: 'Submit a Coupon' });

  private readonly formBuilder = inject(FormBuilder);
  private readonly api = inject(SavingArenaApiService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);

  protected readonly selectedStore = signal<StoreOption | null>(null);
  protected readonly isPickingStore = signal(false);
  protected readonly storeResults = signal<StoreOption[]>([]);
  protected readonly submitted = signal(false);
  protected readonly submitting = signal(false);

  protected readonly storeSearch = new FormControl('', { nonNullable: true });
  protected readonly couponForm = this.formBuilder.nonNullable.group({
    title: ['', [Validators.required]],
    code: [''],
    description: ['', [Validators.required, Validators.minLength(10)]],
    endDateText: [''],
  });

  constructor() {
    this.applySeo();

    // A store page's "Submit coupon" button hands the store over in router state, so arriving from
    // one fills the form in with no lookup at all — the name and logo came with it.
    //
    // There is no `?store=` path any more. This page used to accept the store in a query parameter,
    // which gave one form 36,675 addresses — every store on the site had its own. Those URLs are now
    // 301'd to this one at the web server, so nothing reaches this component with a query string,
    // and reading it here would only keep the duplicate alive.
    const fromState = this.storeFromNavigationState();

    if (fromState) this.selectedStore.set(fromState);
    else this.isPickingStore.set(true);

    this.storeSearch.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const query = (term ?? '').trim();
          if (query.length < 2) {
            return of({ data: [] as FrontendStore[] });
          }
          return this.api
            .getStores({ search: query, limit: 8 })
            .pipe(catchError(() => of({ data: [] as FrontendStore[] })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.storeResults.set((response.data ?? []).map(toStoreOption).filter((store) => store.slug));
      });
  }

  /**
   * The store the visitor came from, handed over in router state rather than in the URL.
   *
   * Two places to look, because they cover different arrivals. `getCurrentNavigation()` holds the
   * state during the navigation that creates this component, which is the normal case: someone
   * clicked "Submit coupon" on a store page. `history.state` holds the same object afterwards, so a
   * refresh still knows which store it was.
   *
   * Neither exists during server rendering, and that is correct — the server is asked for
   * `/submit-coupon` with nothing else, and renders the page with its store picker open. Anything
   * read here is browser-side and untrusted, so every field is coerced rather than believed.
   */
  private storeFromNavigationState(): StoreOption | null {
    const fromNavigation = this.router.getCurrentNavigation()?.extras?.state;
    const fromHistory = isPlatformBrowser(this.platformId)
      ? (this.document.defaultView?.history?.state as Record<string, unknown> | undefined)
      : undefined;

    const raw = (fromNavigation?.['submitCouponStore'] ?? fromHistory?.['submitCouponStore']) as
      | Partial<StoreOption>
      | undefined;

    const slug = String(raw?.slug ?? '').trim();
    const name = String(raw?.name ?? '').trim();
    if (!slug || !name) return null;

    return { slug, name, logoImagePath: String(raw?.logoImagePath ?? '').trim() };
  }

  /** Static form page, and now a single URL — the canonical simply states the address it is at. */
  private applySeo(): void {
    const url = this.seo.absolute(PAGE_PATH);

    this.seo.apply({
      title: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      canonicalPath: PAGE_PATH,
    });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url,
        name: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
    ]);
  }

  protected pickStore(store: StoreOption): void {
    this.selectedStore.set(store);
    this.isPickingStore.set(false);
    this.storeResults.set([]);
    this.storeSearch.setValue('');
  }

  protected changeStore(): void {
    this.isPickingStore.set(true);
    this.storeResults.set([]);
  }

  protected submitCoupon(): void {
    if (!this.selectedStore()) {
      this.isPickingStore.set(true);
      return;
    }
    if (this.couponForm.invalid) {
      this.couponForm.markAllAsTouched();
      return;
    }

    const store = this.selectedStore();
    if (!store) {
      return;
    }

    const values = this.couponForm.getRawValue();
    this.submitting.set(true);

    this.api
      .submitStoreCoupon(store.slug, {
        title: values.title,
        code: values.code,
        description: values.description,
        endDateText: values.endDateText,
      })
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.submitting.set(false);
        if (response) {
          this.submitted.set(true);
          this.couponForm.reset();
        }
      });
  }
}
